import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import { createChartAccount, fetchChartOfAccounts, updateChartAccount } from "../../../api/accounting";
import { formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { ACCOUNT_TYPES, formatAccountType, useLatestRef } from "./accountingUtils";

const ChartOfAccountsPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    account_type: "expense",
    description: "",
  });

  useEffect(() => {
    fetchBranches(true).then((rows) => {
      setBranches(rows || []);
      if (rows?.length) setBranchId(String(rows[0].id));
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    const params = {};
    if (branchId) params.branch_id = branchId;
    if (typeFilter) params.account_type = typeFilter;
    return runLoad(() => fetchChartOfAccounts(params).then(setAccounts), {
      errorMessage: "Failed to load chart of accounts.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load chart of accounts.")))
      .finally(() => setLoading(false));
  }, [branchId, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const setActive = useCallback(
    (row, isActive) => {
      confirm({
        title: isActive ? `Reactivate ${row.code}?` : `Deactivate ${row.code}?`,
        message: isActive ? "Account will appear in journal pickers again." : "Account will be hidden from new entries.",
        confirmLabel: isActive ? "Reactivate" : "Deactivate",
        tone: isActive ? "primary" : "danger",
        successMessage: isActive ? "Account reactivated." : "Account deactivated.",
        onConfirm: async () => {
          await updateChartAccount(row.id, { is_active: isActive });
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Code", selector: (r) => r.code, sortable: true, width: "100px" },
      { name: "Name", selector: (r) => r.name, sortable: true, grow: 2 },
      {
        name: "Type",
        selector: (r) => r.account_type,
        cell: (r) => formatAccountType(r.account_type),
        width: "120px",
      },
      {
        name: "Balance",
        selector: (r) => Number(r.balance),
        cell: (r) => formatTZS(r.balance),
        sortable: true,
        right: true,
      },
      {
        name: "Status",
        cell: (r) => (
          <Badge color={r.is_active ? "success" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge>
        ),
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              {
                icon: r.is_active ? "clear" : "plus",
                label: r.is_active ? "Deactivate account" : "Activate account",
                onClick: () => setActive(r, !r.is_active),
                variant: r.is_active ? "danger" : "primary",
              },
            ]}
          />
        ),
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    [setActive]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createChartAccount({
            branch_id: branchId || null,
            code: form.code,
            name: form.name,
            account_type: form.account_type,
            description: form.description || null,
          }),
        { successMessage: "Account created." }
      );
      setModalOpen(false);
      setForm({ code: "", name: "", account_type: "expense", description: "" });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Chart of Accounts" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Accounts</h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "160px" }}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Global + branch</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "140px" }}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                {ACCOUNT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>
                New account
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <DataTable columns={columns} data={accounts} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New account</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label>Code</Label>
                    <Input required value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="8">
                  <FormGroup>
                    <Label>Name</Label>
                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Type</Label>
                    <Input
                      type="select"
                      value={form.account_type}
                      onChange={(e) => setForm({ ...form, account_type: e.target.value })}>
                      {ACCOUNT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Description</Label>
                    <Input
                      type="textarea"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Creating…" : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default ChartOfAccountsPage;
